// The Disposition worklist (Step 21; CR-062, CR-034 share): every candidate still to disposition, with the reason his
// configuration carried, the Disposition control, and the designation at the row's end (DC-207). An eligible person
// (B3's basis: finalist, alternate, timing withdrawal; S09's timing decline) can be added to the talent community by
// intent, never automatically; the toggle opens Segment · Tags · Reason · Date added · Last contact · Notes · Prior
// requisition. `?designate=<application>` opens that row's toggle prefilled (the Action Center item's deep link).

import { useState } from "react";
import {
  ACTION_LABELS, Banner, Button, Card, Field, Flex, G1_LABELS, Input, KeyValue, Pill, REQUISITION_STATUS, Select, SURFACE_LABELS,
  Stack, Table, Td, TextArea, Th, Toggle, Tr, formatDate, useHs,
} from "@/mc";
import { useSegmentChoices } from "@/views/candidate/options";
import { CLOSURE_LABELS, REASON_ADDED_LABELS, WORKLIST_LABELS } from "./labels";
import { slug } from "./slug";
import { REASONS_ADDED, type ReasonAdded, type RunWrite, type WorklistOut, type WorklistRow } from "./types";

const isAlternate = (row: WorklistRow) => row.stage === "alternate" || row.eligibility === "alternate";

/** B3's basis as a reason added; an unknown basis falls back to none chosen. */
export function reasonFor(row: WorklistRow): ReasonAdded | "" {
  return (REASONS_ADDED as readonly string[]).includes(row.eligibility ?? "") ? (row.eligibility as ReasonAdded) : "";
}

const REASON_OPTIONS = REASONS_ADDED.map((value) => ({ value, label: REASON_ADDED_LABELS[value] }));

function Designation({ rid, row, runWrite, open: openAtFirst, today }: {
  rid: string; row: WorklistRow; runWrite: RunWrite; open: boolean; today: string;
}) {
  const id = row.application_id;
  const [open, setOpen] = useState(openAtFirst);
  const [reason, setReason] = useState<ReasonAdded | "">(reasonFor(row));
  const [segment, setSegment] = useState("");
  // B3 accepts only a configured segment (`taxonomies:segment`), so the choice is that list, read once the form opens
  const segments = useSegmentChoices(open);
  const [tags, setTags] = useState("");
  const [lastContact, setLastContact] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!reason) return;
    setBusy(true);
    try {
      await runWrite({
        tool: "talent.designate",
        input: {
          requisition_id: rid, application_id: id, reason_added: reason,
          segment: segment.trim() || null,
          tags: tags.split(",").map((tag) => tag.trim()).filter(Boolean),
          notes: notes.trim() || null,
          last_contact_at: lastContact || null,
        },
      });
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack>
      <Toggle label={SURFACE_LABELS.addToTalentCommunity} checked={open} onChange={setOpen} hs={`close.designate.${slug(id)}`} />
      {open ? (
        <Stack>
          <Field label={G1_LABELS.reason} required>
            <Select options={REASON_OPTIONS} value={reason} onChange={(value) => setReason(value as ReasonAdded | "")} hs={`close.designate-reason.${slug(id)}`} />
          </Field>
          <Field label={WORKLIST_LABELS.segment} optional>
            <Select options={segments} value={segment} onChange={setSegment} hs={`close.designate-segment.${slug(id)}`} />
          </Field>
          <Field label={WORKLIST_LABELS.tags} optional>
            <Input value={tags} onChange={(event) => setTags(event.target.value)} hs={`close.designate-tags.${slug(id)}`} />
          </Field>
          <Field label={WORKLIST_LABELS.lastContact} optional>
            <Input type="date" value={lastContact} onChange={(event) => setLastContact(event.target.value)} hs={`close.designate-last-contact.${slug(id)}`} />
          </Field>
          <Field label={WORKLIST_LABELS.notes} optional>
            <TextArea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} hs={`close.designate-notes.${slug(id)}`} />
          </Field>
          <KeyValue cols={2} items={[[WORKLIST_LABELS.dateAdded, formatDate(today)], [WORKLIST_LABELS.priorRequisition, rid]]} />
          <Button tone="primary" sm busy={busy} onClick={save} hs={`close.designate-save.${slug(id)}`}
            blocked={reason ? undefined : { reason: G1_LABELS.reason }}>
            {ACTION_LABELS.designate}
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
}

function DispositionControl({ rid, row, codes, runWrite }: {
  rid: string; row: WorklistRow; codes: WorklistOut["codes"]; runWrite: RunWrite;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const id = row.application_id;
  const record = async () => {
    if (!code) return;
    setBusy(true);
    try {
      await runWrite({ tool: "disposition.record", input: { requisition_id: rid, application_id: id, code } });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Flex>
      <Select sm aria-label={`${ACTION_LABELS.disposition} · ${row.candidate}`} options={codes.map((c) => ({ value: c.slug, label: c.label }))}
        value={code} onChange={setCode} hs={`close.disposition-code.${slug(id)}`} />
      <Button sm busy={busy} onClick={record} hs={`close.disposition.${slug(id)}`} blocked={code ? undefined : { reason: G1_LABELS.reason }}>
        {ACTION_LABELS.disposition}
      </Button>
    </Flex>
  );
}

export interface WorklistProps {
  rid: string;
  worklist: WorklistOut;
  runWrite: RunWrite;
  /** The application whose designation toggle opens prefilled (?designate=). */
  designate?: string | undefined;
  /** The business day the page reads (the Date added a designation records). */
  today: string;
  readOnly?: boolean | undefined;
}

export function Worklist({ rid, worklist, runWrite, designate, today, readOnly }: WorklistProps) {
  const can = worklist.can ?? {};
  const mayDisposition = !readOnly && !!(can["disposition.record"] ?? can.record);
  const mayDesignate = !readOnly && !!(can["talent.designate"] ?? can.designate);
  const alternatesOpen = worklist.rows.some((row) => isAlternate(row) && !row.done);
  const reminderHs = useHs("close.alternate-reminder");

  return (
    <Card title={CLOSURE_LABELS.dispositionWorklist} count={worklist.open_count} flush>
      <Stack>
        {alternatesOpen ? (
          <Banner tone="warn">
            <span {...reminderHs}>{WORKLIST_LABELS.alternateReminder}</span>
          </Banner>
        ) : null}
        {worklist.projection === "count" ? (
          <KeyValue items={[[WORKLIST_LABELS.undispositioned, worklist.open_count]]} />
        ) : (
          <Table label={CLOSURE_LABELS.dispositionWorklist}
            head={(
              <Tr>
                <Th>{WORKLIST_LABELS.candidate}</Th>
                <Th>{WORKLIST_LABELS.stage}</Th>
                <Th>{G1_LABELS.reason}</Th>
                <Th>{ACTION_LABELS.disposition}</Th>
                <Th>{WORKLIST_LABELS.talentCommunity}</Th>
              </Tr>
            )}>
            {worklist.rows.map((row) => {
              const reason = row.withdrawal_reason?.label ?? row.decline_reason?.label ?? row.carried_reason ?? "—";
              return (
                <Tr key={row.application_id}>
                  <Td label={WORKLIST_LABELS.candidate} sub={row.token}>{row.candidate}</Td>
                  <Td label={WORKLIST_LABELS.stage}>
                    <Flex>
                      {row.furthest_stage_label}
                      {isAlternate(row) ? <Pill>{WORKLIST_LABELS.alternate}</Pill> : null}
                    </Flex>
                  </Td>
                  <Td label={G1_LABELS.reason}>{reason}</Td>
                  <Td label={ACTION_LABELS.disposition} sub={row.effective_at ? formatDate(row.effective_at) : undefined}>
                    {row.done || !mayDisposition
                      ? row.code_label ?? "—"
                      : <DispositionControl rid={rid} row={row} codes={worklist.codes} runWrite={runWrite} />}
                  </Td>
                  <Td label={WORKLIST_LABELS.talentCommunity}>
                    {row.member ? (
                      <Pill>{WORKLIST_LABELS.talentCommunity}</Pill>
                    ) : row.eligibility && mayDesignate ? (
                      <Designation rid={rid} row={row} runWrite={runWrite} open={designate === row.application_id} today={today} />
                    ) : row.eligibility ? (
                      <Pill>{WORKLIST_LABELS.eligible}</Pill>
                    ) : "—"}
                  </Td>
                </Tr>
              );
            })}
          </Table>
        )}
        {worklist.requisition_status === "offer_accepted" ? <Pill>{REQUISITION_STATUS.offerAccepted}</Pill> : null}
      </Stack>
    </Card>
  );
}
